import React, { useState } from 'react';
import {
  CryptoExplorerProps,
  LIMIT,
  TABLE_COLUMNS,
} from './CryptoExplorer.config';
import Table from '../../ExplorerTable';
import { getLogo } from '../../../helpers/functions';
import { useSort } from '../../../hooks/useSort';
import { useQuery } from '@apollo/client';
import { LIST_EXPLORER_ASSETS } from '../../../http/apollo-queries';
import RowDetails from '../../ExplorerTable/RowDetails';
import { upperCase } from 'lodash';
import { Loading } from '../../UI';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { ProfitLoss } from '../../ProfitLoss';
import { useErrorHandler } from 'react-error-boundary';
import { Asset } from '../../Asset/Asset';
import { IAsset } from '../../../types/model';

const div = (v) => <div className={'truncate'}>{v}</div>;
const span = (v) => <span className={'text-grey-50'}>{v}</span>;

const CryptoExplorer: React.FC<CryptoExplorerProps> = ({
  width,
  setLastWidgetUpdateAt,
}) => {
  const onError = useErrorHandler();

  // State
  const [selectedCrypto, setSelectedCrypto] = useState<null | JSX.Element>(
    null
  );

  // Hooks
  const { type, handleSort, getSortValue } = useSort(
    TABLE_COLUMNS.map(({ t }) => t),
    'pricePerformanceDaily',
    'sortDown'
  );

  // Queries
  const { data, loading } = useQuery<{
    listExplorerAssets: { items: IAsset[] };
  }>(LIST_EXPLORER_ASSETS, {
    variables: {
      type: 'CRYPTO',
      limit: LIMIT,
    },
    onError,
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  let assets = [...(data?.listExplorerAssets?.items || [])];

  if (type === 'lastPrice') {
    assets = assets.sort((a, b) =>
      getSortValue(a.recent?.[0]?.usd!, b.recent?.[0].usd!)
    );
  } else {
    assets = assets.sort((a, b) => getSortValue(a[type], b[type]));
  }

  if (loading)
    return (
      <div className={'w-full h-full flex justify-center '}>
        <Loading status={true} size={'large'} />
      </div>
    );

  return (
    selectedCrypto ?? (
      <div className="w-full h-full text-sm">
        <Table
          columns={TABLE_COLUMNS}
          handleSort={handleSort}
          width={width}
          type={type}
          data={assets.map((asset) => {
            return {
              data: [
                <div className={'flex items-center gap-3 pl-4 truncate'}>
                  <Asset
                    id={asset.id}
                    type={asset.type!}
                    symbol={asset.symbol}
                    name={asset.name}
                    logo={asset.logo}
                    exchange={asset.exchange}
                  />
                </div>,
                div(asset.symbol.toUpperCase()),
                div(
                  <CustomNumber
                    displayType="text"
                    value={asset.recent?.[0]?.usd}
                    suffix={'$'}
                  />
                ),
                div(
                  <ProfitLoss
                    displayType="text"
                    size={'small'}
                    percentageClassName="text-sm"
                    percentage={asset.pricePerformanceDaily}
                  />
                ),
                div(
                  <CustomNumber
                    displayType="text"
                    value={asset?.weeklyHighest}
                    suffix={'$'}
                  />
                ),
                div(
                  <CustomNumber
                    displayType="text"
                    value={asset?.weeklyLowest}
                    suffix={'$'}
                  />
                ),
                div(
                  <ProfitLoss
                    displayType="text"
                    size={'small'}
                    percentageClassName="text-sm"
                    percentage={asset.pricePerformanceWeekly}
                  />
                ),
                div(<CustomNumber displayType="text" value={asset?.volume} />),
                div(
                  <CustomNumber
                    displayType="text"
                    value={asset?.marketCap}
                    suffix={'$'}
                  />
                ),
              ],
              extendedProps: {
                asset,
              },
            };
          })}
          handleClick={({ asset } = {}) => {
            if (width !== 1) return;
            const rowData = [
              upperCase(asset.symbol),
              asset.recent?.[0]?.usd,
              asset.pricePerformanceDaily || '0',
              asset?.weeklyHighest || '-',
              asset?.weeklyLowest || '-',
              asset.pricePerformanceWeekly || '-',
              asset?.volume ?? '-',
              asset?.marketCap ?? '-',
            ];
            setSelectedCrypto(
              <RowDetails
                title={asset.name}
                image={getLogo(asset.type, asset.logo)}
                columns={TABLE_COLUMNS.map(({ t }) => t)}
                goBack={() => setSelectedCrypto(null)}
              >
                {rowData.map((el, index) => (
                  <React.Fragment key={index}>{span(el)}</React.Fragment>
                ))}
              </RowDetails>
            );
          }}
        />
      </div>
    )
  );
};

export default CryptoExplorer;
